// Kimiya header behaviour — the sticky offset, the category nav, the mobile
// drawer and the bag.
//
// This lived in kimiya-pdp.js, which the product section loads and nothing
// else does. The header renders on every template, so on every page except a
// product page the menu button was inert: below the breakpoint the inline nav
// is hidden and the toggle is the only way to reach it, which left the whole
// site without navigation on a phone. Splitting it out and loading it from the
// header snippet ties the script to the markup it drives.

(function () {
  'use strict';

  // ---- sticky offset ------------------------------------------------------
  // The sticky header is pulled up by exactly the announce bar's height, so
  // the bar scrolls away and the nav pins. The bar wraps to two lines on a
  // phone and one on a desktop, so the height is measured, not assumed.
  var announce = document.getElementById('kimAnnounce');
  if (announce) {
    var measure = function () {
      document.documentElement.style.setProperty('--kim-announce-h', announce.offsetHeight + 'px');
    };
    measure();
    window.addEventListener('resize', measure);
    // Webfonts land after first paint and can reflow the bar to a second line.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  var header = document.querySelector('.kim-chrome');
  var menuToggle = document.getElementById('kimMenuToggle');
  var menu = document.getElementById('kimMenu');

  // ---- category disclosures ----------------------------------------------
  // Click, not hover. A hover-only dropdown cannot be reached from a keyboard
  // and on a touch screen the first tap opens it and immediately follows the
  // link underneath. The button/panel pair works identically in the desktop
  // row and in the mobile drawer, which is why there is one set of markup.
  var disclosures = header ? [].slice.call(header.querySelectorAll('.kim-catnav__disclosure')) : [];

  function closeAll(except) {
    disclosures.forEach(function (button) {
      if (button === except) return;
      var panel = document.getElementById(button.getAttribute('aria-controls'));
      button.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  }

  function setDisclosure(button, open) {
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    button.setAttribute('aria-expanded', String(open));
    if (panel) panel.hidden = !open;
    if (open) closeAll(button);
  }

  disclosures.forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.stopPropagation();
      setDisclosure(button, button.getAttribute('aria-expanded') !== 'true');
    });
  });

  // ---- mobile drawer ------------------------------------------------------
  function setMenu(open) {
    if (!menuToggle || !menu) return;
    menuToggle.setAttribute('aria-expanded', String(open));
    menu.classList.toggle('menu--open', open);
    // Leaving a category expanded means the drawer reopens mid-list next time.
    if (!open) closeAll(null);
  }

  if (menuToggle && menu) {
    menuToggle.addEventListener('click', function (event) {
      event.stopPropagation();
      setMenu(menuToggle.getAttribute('aria-expanded') !== 'true');
    });
  }

  // A panel that only closes from the control that opened it is a trap — on a
  // phone that control is a 44px corner of a full-width overlay, and on a
  // desktop a stray dropdown sits over the page until you find it again.
  document.addEventListener('click', function (event) {
    if (header && !header.contains(event.target)) {
      closeAll(null);
      if (menu && menu.classList.contains('menu--open')) setMenu(false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = disclosures.filter(function (b) {
      return b.getAttribute('aria-expanded') === 'true';
    });
    if (open.length) {
      closeAll(null);
      open[0].focus();
      return;
    }
    if (menu && menu.classList.contains('menu--open')) {
      setMenu(false);
      if (menuToggle) menuToggle.focus();
    }
  });

  // Crossing the breakpoint leaves the drawer's open class on an element that
  // is now the desktop row, which pins it open as an overlay.
  var wide = window.matchMedia('(min-width: 1400px)');
  var onBreakpoint = function () {
    if (wide.matches && menu && menu.classList.contains('menu--open')) setMenu(false);
  };
  if (wide.addEventListener) wide.addEventListener('change', onBreakpoint);
  else if (wide.addListener) wide.addListener(onBreakpoint);

  // ---- the bag ------------------------------------------------------------
  // With the drawer switched on, the bag opens it rather than leaving the page.
  // Dawn's own CartDrawer binds itself to #cart-icon-bubble, which it also
  // overwrites with its icon markup after every cart change — so that id is on
  // a hidden sink and the real link is bound here instead.
  var bag = header ? header.querySelector('[data-kim-cart-open]') : null;
  if (bag) {
    bag.setAttribute('aria-haspopup', 'dialog');
    bag.addEventListener('click', function (event) {
      var drawer = document.querySelector('cart-drawer');
      // No drawer on the page (or the script has not defined it yet) means the
      // link should still take you to the cart, so only swallow the click once
      // there is something to open.
      if (!drawer || typeof drawer.open !== 'function') return;
      event.preventDefault();
      drawer.open(bag);
    });
  }

  // Mirror Dawn's re-rendered count into every place the design shows one —
  // the header's "bag (n)" and the sticky tab's badge.
  var sink = document.getElementById('cart-icon-bubble');
  var tab = document.querySelector('[data-kim-cart-toggle]');
  if (sink && 'MutationObserver' in window) {
    var syncCount = function () {
      var bubble = sink.querySelector('.cart-count-bubble span[aria-hidden]');
      var count = bubble ? bubble.textContent.trim() : '0';
      var label = 'Bag, ' + count + (count === '1' ? ' item' : ' items');
      document.querySelectorAll('[data-kim-cart-count]').forEach(function (node) {
        node.textContent = count;
      });
      if (bag) bag.setAttribute('aria-label', label);
      if (tab) {
        tab.setAttribute('aria-label', label);
        tab.classList.toggle('is-empty', count === '0');
      }
    };
    new MutationObserver(syncCount).observe(sink, { childList: true, subtree: true });
  }

  // ---- empty-state sync ---------------------------------------------------
  //
  // Shopify puts "is-empty" on <cart-drawer> when the page renders with an
  // empty cart, and that class hides the drawer's header and re-lays its body
  // for the "your cart is empty" panel. But after an AJAX add its own
  // renderContents() only clears the class from .drawer__inner — a different
  // element — so the host keeps it. The result, for anyone whose cart was
  // empty when the page loaded, is a drawer that fills with products and then
  // renders them into the empty-cart layout with no header.
  //
  // The item count in the freshly rendered drawer is the truth, so the class
  // is derived from that on every change: an add, a quantity change, a
  // removal down to zero.
  var drawerHost = document.querySelector('cart-drawer');
  var drawerBody = document.getElementById('CartDrawer');
  if (drawerHost && drawerBody && 'MutationObserver' in window) {
    var syncEmpty = function () {
      var empty = drawerHost.querySelectorAll('.cart-item').length === 0;
      drawerHost.classList.toggle('is-empty', empty);
    };
    new MutationObserver(syncEmpty).observe(drawerBody, { childList: true, subtree: true });
  }

  // ---- the sticky bag tab -------------------------------------------------
  //
  // One control that both opens and closes the drawer. It tracks the drawer's
  // own state rather than assuming it, because the drawer is also opened by an
  // add to cart and closed by its overlay and by Escape.
  if (tab) {
    var drawerEl = document.querySelector('cart-drawer');

    if (drawerEl && tab.classList.contains('is-empty') === false && drawerEl.classList.contains('is-empty')) {
      tab.classList.add('is-empty');
    }

    tab.addEventListener('click', function () {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }
      if (drawer.classList.contains('active')) drawer.close();
      else drawer.open(tab);
    });

    if (drawerEl && 'MutationObserver' in window) {
      var syncTab = function () {
        var open = drawerEl.classList.contains('active');
        tab.setAttribute('aria-expanded', String(open));
        // The tab slides clear of the drawer rather than sitting under it.
        document.body.classList.toggle('kim-cart-open', open);
      };
      new MutationObserver(syncTab).observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
      syncTab();
    }
  }
})();
